import { createUnifiedTheme, genPageTheme, palettes } from '@backstage/theme';
import type { UnifiedTheme } from '@backstage/theme';

// Official Kyndryl Brand Colors from Brand Standards V1.9
export const kyndrylColors = {
  // Hero Color
  warmRed: '#FF462D',

  // Supporting Colors
  deepForest: '#042315',
  spruce: '#29707A',
  darkStone: '#3D3C3C',

  // Complementary Colors
  springGreen: '#4CDD84',
  earth: '#9E9287',
  cloud: '#F2F1EE',
  skye: '#E4F4F1',
  white: '#FFFFFF',

  // Digital Only
  digitalDarkSpringGreen: '#187E3F',
  digitalDarkEarth: '#565049',

  // AI Accent — a vibrant gradient-aware purple/violet for AI features
  aiPurple: '#7C3AED',
  aiPurpleLight: '#A78BFA',
  aiPurpleDark: '#5B21B6',
  aiGlow: 'rgba(124, 58, 237, 0.12)',
};

// ---------------------------------------------------------------------------
// Kyndryl "K" Brand Line Art Pattern
// Overlapping K letterforms inspired by Kyndryl brand identity.
// Two interleaved sets of K's with stems every 100px, diags spanning 200px.
// 100px horizontal offset between / and \ at vertex, 0px vertical.
// Canvas: 1368×200, compact 110px band (y=45–155), no leftmost stem.
// Rendered at fixed 200px height, center-cropped at smaller header sizes.
// ---------------------------------------------------------------------------
const kyndrylKPattern = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1368' height='200'%3E%3Cg stroke='rgba(255,70,45,0.25)' stroke-width='2' fill='none'%3E%3Cline x1='900' y1='100' x2='1100' y2='45'/%3E%3Cline x1='950' y1='100' x2='1100' y2='155'/%3E%3Cline x1='1100' y1='45' x2='1100' y2='155'/%3E%3Cline x1='1100' y1='100' x2='1300' y2='45'/%3E%3Cline x1='1150' y1='100' x2='1300' y2='155'/%3E%3Cline x1='1300' y1='45' x2='1300' y2='155'/%3E%3Cline x1='1300' y1='100' x2='1500' y2='45'/%3E%3Cline x1='1350' y1='100' x2='1500' y2='155'/%3E%3Cline x1='1500' y1='45' x2='1500' y2='155'/%3E%3Cline x1='1500' y1='100' x2='1700' y2='45'/%3E%3Cline x1='1550' y1='100' x2='1700' y2='155'/%3E%3Cline x1='1000' y1='45' x2='1000' y2='155'/%3E%3Cline x1='1000' y1='100' x2='1200' y2='45'/%3E%3Cline x1='1050' y1='100' x2='1200' y2='155'/%3E%3Cline x1='1200' y1='45' x2='1200' y2='155'/%3E%3Cline x1='1200' y1='100' x2='1400' y2='45'/%3E%3Cline x1='1250' y1='100' x2='1400' y2='155'/%3E%3Cline x1='1400' y1='45' x2='1400' y2='155'/%3E%3Cline x1='1400' y1='100' x2='1600' y2='45'/%3E%3Cline x1='1450' y1='100' x2='1600' y2='155'/%3E%3C/g%3E%3C/svg%3E")`;

// Secondary variant — slightly different opacity
const kyndrylKPattern2 = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1368' height='200'%3E%3Cg stroke='rgba(255,70,45,0.2)' stroke-width='2' fill='none'%3E%3Cline x1='900' y1='100' x2='1100' y2='45'/%3E%3Cline x1='950' y1='100' x2='1100' y2='155'/%3E%3Cline x1='1100' y1='45' x2='1100' y2='155'/%3E%3Cline x1='1100' y1='100' x2='1300' y2='45'/%3E%3Cline x1='1150' y1='100' x2='1300' y2='155'/%3E%3Cline x1='1300' y1='45' x2='1300' y2='155'/%3E%3Cline x1='1300' y1='100' x2='1500' y2='45'/%3E%3Cline x1='1350' y1='100' x2='1500' y2='155'/%3E%3Cline x1='1500' y1='45' x2='1500' y2='155'/%3E%3Cline x1='1500' y1='100' x2='1700' y2='45'/%3E%3Cline x1='1550' y1='100' x2='1700' y2='155'/%3E%3Cline x1='1000' y1='45' x2='1000' y2='155'/%3E%3Cline x1='1000' y1='100' x2='1200' y2='45'/%3E%3Cline x1='1050' y1='100' x2='1200' y2='155'/%3E%3Cline x1='1200' y1='45' x2='1200' y2='155'/%3E%3Cline x1='1200' y1='100' x2='1400' y2='45'/%3E%3Cline x1='1250' y1='100' x2='1400' y2='155'/%3E%3Cline x1='1400' y1='45' x2='1400' y2='155'/%3E%3Cline x1='1400' y1='100' x2='1600' y2='45'/%3E%3Cline x1='1450' y1='100' x2='1600' y2='155'/%3E%3C/g%3E%3C/svg%3E")`;

// ---------------------------------------------------------------------------
// Shared MUI + Backstage component overrides (used by both themes)
// Uses MUI v5 `components` format compatible with createUnifiedTheme
// ---------------------------------------------------------------------------
const kyndrylComponentOverrides = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '20px',
        textTransform: 'none' as const,
        fontFamily: '"TWK Everett", sans-serif',
        fontWeight: 500,
        letterSpacing: '0.01em',
        padding: '8px 20px',
      },
      containedPrimary: {
        '&:hover': {
          backgroundColor: kyndrylColors.springGreen,
          color: kyndrylColors.deepForest,
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: '16px',
        display: 'flex',
        flexDirection: 'column' as const,
        overflow: 'hidden',
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        flex: 1,
        padding: '20px 24px',
        '&:last-child': {
          paddingBottom: 20,
        },
      },
    },
  },
  MuiCardActions: {
    styleOverrides: {
      root: {
        marginTop: 'auto',
        padding: '12px 24px',
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: {
        padding: '20px 24px 12px',
      },
      title: {
        fontSize: '1rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      subheader: {
        fontSize: '0.8rem',
        marginTop: 2,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        fontSize: '0.7rem',
        borderRadius: '8px',
        fontWeight: 500,
        height: 26,
      },
      sizeSmall: {
        height: 24,
        fontSize: '0.68rem',
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        textTransform: 'uppercase' as const,
        letterSpacing: '0.06em',
        fontSize: '0.7rem',
        fontWeight: 600,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: '1px solid rgba(0,0,0,0.05)',
        padding: '14px 16px',
        fontSize: '0.85rem',
      },
      head: {
        fontWeight: 600,
        color: 'inherit',
        fontSize: '0.7rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase' as const,
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:last-child td': {
          borderBottom: 'none',
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: '10px',
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: kyndrylColors.digitalDarkSpringGreen,
          borderWidth: '1.5px',
        },
      },
      notchedOutline: {
        borderColor: 'rgba(0,0,0,0.12)',
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        backgroundColor: kyndrylColors.digitalDarkSpringGreen,
        height: 3,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none' as const,
        fontFamily: '"TWK Everett", sans-serif',
        fontWeight: 500,
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      barColorPrimary: {
        backgroundColor: kyndrylColors.digitalDarkSpringGreen,
      },
    },
  },
  MuiCircularProgress: {
    styleOverrides: {
      colorPrimary: {
        color: kyndrylColors.digitalDarkSpringGreen,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: {
        borderRadius: '16px',
      },
      elevation1: {
        boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)',
      },
      elevation2: {
        boxShadow: '0 2px 6px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontFamily: '"TWK Everett", sans-serif',
        fontSize: '0.75rem',
        borderRadius: '6px',
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: '16px',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
      },
    },
  },
  MuiStepIcon: {
    styleOverrides: {
      root: {
        '&.Mui-active': {
          color: kyndrylColors.digitalDarkSpringGreen,
        },
        '&.Mui-completed': {
          color: kyndrylColors.springGreen,
        },
      },
    },
  },
  MuiStepLabel: {
    styleOverrides: {
      label: {
        fontFamily: '"TWK Everett", sans-serif',
        '&.Mui-active': {
          fontWeight: 600,
        },
      },
    },
  },
  MuiStepConnector: {
    styleOverrides: {
      line: {
        borderColor: kyndrylColors.digitalDarkSpringGreen,
      },
    },
  },
  // Override Backstage Header to render K art at fixed 200px, right-aligned, center-cropped
  BackstageHeader: {
    styleOverrides: {
      header: {
        backgroundSize: 'auto 200px, cover',
        backgroundPosition: 'right center, center',
        backgroundRepeat: 'no-repeat, no-repeat',
        boxShadow: 'none',
        paddingBottom: 24,
      },
      title: {
        fontSize: '1.5rem',
        fontWeight: 600,
      },
      subtitle: {
        fontSize: '0.85rem',
        opacity: 0.7,
      },
    },
  },
  BackstageItemCardHeader: {
    styleOverrides: {
      root: {
        // Clean gradient header instead of K art on small cards
        backgroundImage: 'none !important',
        backgroundSize: 'cover',
        padding: '16px 20px 14px',
        '& h4': {
          fontSize: '0.95rem',
          fontWeight: 600,
          lineHeight: 1.3,
        },
        '& h3': {
          fontSize: '0.75rem',
          fontWeight: 500,
          opacity: 0.8,
        },
      },
    },
  },
  BackstageHeaderTabs: {
    styleOverrides: {
      tabsWrapper: {
        backgroundColor: 'transparent',
      },
      defaultTab: {
        fontFamily: '"TWK Everett", sans-serif',
        fontWeight: 500,
        textTransform: 'none' as const,
        letterSpacing: '0.01em',
        fontSize: '0.875rem',
        padding: '12px 20px',
      },
    },
  },
  BackstageContentHeader: {
    styleOverrides: {
      title: {
        fontFamily: '"TWK Everett", sans-serif',
        fontWeight: 600,
      },
    },
  },
  BackstageInfoCard: {
    styleOverrides: {
      header: {
        borderBottom: 'none',
        paddingBottom: 0,
      },
    },
  },
  // Remove Page's independent scroll context so the global sticky header works
  BackstagePage: {
    styleOverrides: {
      root: {
        height: 'auto',
        overflowY: 'visible' as const,
      },
    },
  },
  // Sidebar nav item: pill-style selected state instead of left border
  BackstageSidebarItem: {
    styleOverrides: {
      root: {
        borderRadius: '10px',
        margin: '2px 8px',
        transition: 'background-color 0.15s ease',
      },
      selected: {
        borderLeft: 'none !important',
        borderRadius: '10px',
        paddingLeft: '0 !important',
        marginLeft: '8px !important',
      },
      label: {
        fontSize: '0.85rem',
        fontWeight: 500,
      },
      iconContainer: {
        width: 40,
        minWidth: 40,
        marginRight: 0, // Override default -16px that crushes icons into labels
      },
    },
  },
  BackstageSidebarDivider: {
    styleOverrides: {
      root: {
        background: 'rgba(0,0,0,0.06)',
        margin: '8px 16px',
        height: 1,
      },
    },
  },
};

export const kyndrylDarkTheme: UnifiedTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    mode: 'dark',
    primary: {
      main: kyndrylColors.digitalDarkSpringGreen, // Digital Dark Spring Green - more refined
      light: kyndrylColors.springGreen,
      dark: '#0D5C2F', // Darker variant
    },
    secondary: {
      main: kyndrylColors.spruce,
      light: '#3A8593',
      dark: '#1D5560',
    },
    error: {
      main: kyndrylColors.warmRed,
    },
    background: {
      default: '#000000', // True black for sleeker look
      paper: '#0A0F0D', // Very dark forest, almost black
    },
    navigation: {
      background: '#000000',
      indicator: kyndrylColors.digitalDarkSpringGreen,
      color: kyndrylColors.cloud,
      selectedColor: kyndrylColors.springGreen,
      navItem: {
        hoverBackground: 'rgba(24, 126, 63, 0.08)',
      },
    },
    action: {
      active: kyndrylColors.digitalDarkSpringGreen,
      hover: 'rgba(24, 126, 63, 0.08)',
      selected: 'rgba(24, 126, 63, 0.12)',
      disabled: 'rgba(242, 241, 238, 0.3)',
      disabledBackground: 'rgba(242, 241, 238, 0.12)',
    },
    text: {
      primary: kyndrylColors.cloud, // Softer white for less eye strain
      secondary: '#A8A29D', // Muted earth tone
    },
    banner: {
      info: kyndrylColors.digitalDarkSpringGreen,
      error: kyndrylColors.warmRed,
      text: kyndrylColors.cloud,
      link: kyndrylColors.springGreen,
    },
    border: '#1A1F1D', // Very subtle borders
    divider: 'rgba(76, 221, 132, 0.08)', // Subtle green tint
    status: {
      ok: kyndrylColors.digitalDarkSpringGreen,
      warning: kyndrylColors.warmRed,
      error: kyndrylColors.warmRed,
      running: kyndrylColors.spruce,
      pending: kyndrylColors.earth,
      aborted: kyndrylColors.digitalDarkEarth,
    },
  },
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern }),
    documentation: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern2 }),
    tool: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern2 }),
    service: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern }),
    website: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern }),
    library: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern2 }),
    other: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern }),
    app: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern }),
    apis: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern2 }),
    card: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern }),
  },
  fontFamily: '"TWK Everett", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif',
  components: {
    ...kyndrylComponentOverrides,
    // Dark-theme specific: subtle glow cards
    MuiCard: {
      ...kyndrylComponentOverrides.MuiCard,
      styleOverrides: {
        ...kyndrylComponentOverrides.MuiCard.styleOverrides,
        root: {
          ...kyndrylComponentOverrides.MuiCard.styleOverrides.root,
          border: '1px solid rgba(255, 255, 255, 0.06)',
          boxShadow: '0 2px 12px rgba(0,0,0,0.5)',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.1)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.6)',
          },
        },
      },
    },
    // Dark sidebar: subtle right accent
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          borderRight: '1px solid rgba(255,255,255,0.06)',
        },
      },
    },
    // Dark sidebar selected: green tinted pill
    BackstageSidebarItem: {
      ...kyndrylComponentOverrides.BackstageSidebarItem,
      styleOverrides: {
        ...kyndrylComponentOverrides.BackstageSidebarItem.styleOverrides,
        selected: {
          ...kyndrylComponentOverrides.BackstageSidebarItem.styleOverrides.selected,
          backgroundColor: 'rgba(24, 126, 63, 0.15)',
        },
      },
    },
  },
  typography: {
    fontFamily: '"TWK Everett", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif',
    htmlFontSize: 16,
    h1: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 700,
      fontSize: '3rem',
      marginBottom: 16,
    },
    h2: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '2.25rem',
      marginBottom: 14,
    },
    h3: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '1.75rem',
      marginBottom: 12,
    },
    h4: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '1.5rem',
      marginBottom: 10,
    },
    h5: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem',
      marginBottom: 8,
    },
    h6: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '1rem',
      marginBottom: 6,
    },
  },
});

export const kyndrylLightTheme: UnifiedTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: {
      main: kyndrylColors.digitalDarkSpringGreen,
      light: kyndrylColors.springGreen,
      dark: kyndrylColors.deepForest,
    },
    secondary: {
      main: kyndrylColors.spruce,
      light: kyndrylColors.skye,
      dark: kyndrylColors.deepForest,
    },
    error: {
      main: kyndrylColors.warmRed,
    },
    background: {
      default: '#F8F8F6', // Near-white warm gray — lighter than cloud for card contrast
      paper: kyndrylColors.white,
    },
    navigation: {
      background: kyndrylColors.white,
      indicator: kyndrylColors.warmRed,
      color: kyndrylColors.darkStone,
      selectedColor: kyndrylColors.warmRed,
      submenu: {
        background: kyndrylColors.white,
      },
      navItem: {
        hoverBackground: 'rgba(255, 70, 45, 0.06)',
      },
    },
    action: {
      active: kyndrylColors.digitalDarkSpringGreen,
      hover: 'rgba(24, 126, 63, 0.08)',
      selected: 'rgba(24, 126, 63, 0.16)',
    },
    text: {
      primary: '#1A1A1A', // Near-black for crisp readability
      secondary: '#6B7280', // Professional gray
    },
    border: 'rgba(0,0,0,0.08)',
    status: {
      ok: kyndrylColors.digitalDarkSpringGreen,
      warning: kyndrylColors.warmRed,
      error: kyndrylColors.warmRed,
      running: kyndrylColors.spruce,
      pending: kyndrylColors.earth,
      aborted: kyndrylColors.darkStone,
    },
  },
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({ colors: ['#F8F8F6', '#F8F8F6'], shape: kyndrylKPattern, options: { fontColor: '#1A1A1A' } }),
    documentation: genPageTheme({ colors: ['#F8F8F6', '#F8F8F6'], shape: kyndrylKPattern2, options: { fontColor: '#1A1A1A' } }),
    tool: genPageTheme({ colors: ['#F8F8F6', '#F8F8F6'], shape: kyndrylKPattern2, options: { fontColor: '#1A1A1A' } }),
    service: genPageTheme({ colors: ['#F8F8F6', '#F8F8F6'], shape: kyndrylKPattern, options: { fontColor: '#1A1A1A' } }),
    website: genPageTheme({ colors: ['#F8F8F6', '#F8F8F6'], shape: kyndrylKPattern, options: { fontColor: '#1A1A1A' } }),
    library: genPageTheme({ colors: ['#F8F8F6', '#F8F8F6'], shape: kyndrylKPattern2, options: { fontColor: '#1A1A1A' } }),
    other: genPageTheme({ colors: ['#F8F8F6', '#F8F8F6'], shape: kyndrylKPattern, options: { fontColor: '#1A1A1A' } }),
    app: genPageTheme({ colors: ['#F8F8F6', '#F8F8F6'], shape: kyndrylKPattern, options: { fontColor: '#1A1A1A' } }),
    apis: genPageTheme({ colors: ['#F8F8F6', '#F8F8F6'], shape: kyndrylKPattern2, options: { fontColor: '#1A1A1A' } }),
    card: genPageTheme({ colors: ['#F8F8F6', '#F8F8F6'], shape: kyndrylKPattern, options: { fontColor: '#1A1A1A' } }),
  },
  fontFamily: '"TWK Everett", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif',
  components: {
    ...kyndrylComponentOverrides,
    // Light-theme: clean SaaS dashboard cards
    MuiCard: {
      ...kyndrylComponentOverrides.MuiCard,
      styleOverrides: {
        ...kyndrylComponentOverrides.MuiCard.styleOverrides,
        root: {
          ...kyndrylComponentOverrides.MuiCard.styleOverrides.root,
          border: '1px solid rgba(0, 0, 0, 0.06)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            borderColor: 'rgba(0, 0, 0, 0.12)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
          },
        },
      },
    },
    // Light sidebar: subtle right border
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          borderRight: '1px solid rgba(0,0,0,0.08)',
        },
      },
    },
    // Sidebar selected item: warm red tinted pill background
    BackstageSidebarItem: {
      ...kyndrylComponentOverrides.BackstageSidebarItem,
      styleOverrides: {
        ...kyndrylComponentOverrides.BackstageSidebarItem.styleOverrides,
        selected: {
          ...kyndrylComponentOverrides.BackstageSidebarItem.styleOverrides.selected,
          backgroundColor: 'rgba(255, 70, 45, 0.08)',
        },
      },
    },
  },
  typography: {
    fontFamily: '"TWK Everett", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif',
    htmlFontSize: 16,
    h1: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 700,
      fontSize: '3rem',
      marginBottom: 16,
    },
    h2: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '2.25rem',
      marginBottom: 14,
    },
    h3: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '1.75rem',
      marginBottom: 12,
    },
    h4: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '1.5rem',
      marginBottom: 10,
    },
    h5: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem',
      marginBottom: 8,
    },
    h6: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '1rem',
      marginBottom: 6,
    },
  },
});
